import { peopleSubNavItems } from "@/components/community/peopleSubNav";
import { getModuleAccent, getModuleForPath, type ModuleAccent } from "@/lib/module-accents";
import { navModuleForNextPath } from "@/lib/module-nav-routes";
import type { UiVariant } from "@/lib/ui-uplift";
import { activeSubNavItemId, type ModuleSubNavItem } from "./moduleSubNavMatch";

/**
 * Every item the People bar could show (a viewer with every role and permission): the pages the
 * bar owns, which include the records the sidebar has no prefix for (`/households/…`,
 * `/family-groups/…`).
 */
const PEOPLE_ITEMS: ModuleSubNavItem[] = (() => {
  const { items, moreItems } = peopleSubNavItems({ withGroups: true, withCare: true, withPeople: true, withSettings: true });
  return [...items, ...moreItems];
})();

/**
 * The module a page belongs to under the next chassis, as the navigation places it: the People
 * bar's pages, then the sidebar entry that highlights the path (src/lib/module-nav-routes.ts, the
 * table AppSidebar reads). The Operations bar's pages are all under the sidebar's Operations
 * prefixes, which moduleForRoute.test.ts asserts for every item the bar can show. `null` for a page
 * the navigation places nowhere.
 */
function logicalModuleId(pathname: string): string | null {
  const path = pathname.toLowerCase();
  if (activeSubNavItemId(PEOPLE_ITEMS, path) !== null) return "people";
  return navModuleForNextPath(path);
}

/**
 * The module accent AppLayout scopes a page to (#7912).
 *
 * Under the next chassis it is the module the page's own navigation names: the sub-nav bar above it
 * and the sidebar entry beside it. `getModuleForPath` is a list of keywords and knows nothing of
 * the pages the navigation moved or never listed (Groups, Care, Documents, Requests, Services, …),
 * so it gave them Home's amber and left the page's tabs, filter chips and timeline in the wrong
 * colour. A page the navigation places nowhere keeps `getModuleForPath`'s answer (Home, when it
 * has none): that is where it belongs, not a gap.
 *
 * Classic keeps `getModuleForPath` for every route: with the flag off nothing about a page's
 * colours changes.
 */
export function getModuleForRoute(pathname: string, variant: UiVariant): ModuleAccent {
  if (variant !== "next") return getModuleForPath(pathname);
  const moduleId = logicalModuleId(pathname);
  return moduleId === null ? getModuleForPath(pathname) : getModuleAccent(moduleId);
}
